import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { ABOUT } from '@/constants/testIds';

const Counter = ({ to, suffix = '', duration = 1.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min((t - start) / (duration * 1000), 1);
      setVal(Math.floor(p * to));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
};

export const About = () => {
  return (
    <section id="about" data-testid={ABOUT.root} className="py-24 lg:py-32 bg-surface">
      <div className="container-luxe max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
        >
          <span className="overline">Our Story · In-House Store</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal mt-3 leading-tight tracking-tight">
            A <em className="text-maroon not-italic font-medium">love letter</em> to Indian craftsmanship.
          </h2>
          <div className="mt-5 h-px w-16 bg-gold" />
          <div className="mt-8 space-y-5 text-charcoal/75 leading-relaxed text-base lg:text-lg">
            <p>
              Step into the world of elegance and tradition at <span className="text-charcoal font-medium">Trendy Closet</span>,
              where every piece tells a story of timeless beauty. Discover a curated collection of Indian traditional,
              ethnic, and designer attire for men, women, and kids.
            </p>
            <p>
              From vibrant sarees and intricate embroidery to stylish kurta sets and trendy kids' outfits, we have
              something for every occasion. Our <span className="text-charcoal font-medium">in-house boutique</span> spans
              over <span className="text-charcoal font-medium">600+ square feet</span>, offering a personalised shopping
              experience.
            </p>
            <p>
              Visit us today or shop from the comfort of your home. Elevate your wardrobe with Trendy Closet —
              fashion for the entire family.
            </p>
            <p className="text-charcoal/85 italic font-display text-lg lg:text-xl">
              “We would appreciate a call or text before you plan your visit.”
            </p>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            <div>
              <div className="font-display text-3xl lg:text-4xl text-maroon"><Counter to={5} suffix=".0" /></div>
              <div className="overline mt-1">Google Rating</div>
            </div>
            <div>
              <div className="font-display text-3xl lg:text-4xl text-maroon"><Counter to={40} suffix="+" /></div>
              <div className="overline mt-1">5★ Reviews</div>
            </div>
            <div>
              <div className="font-display text-3xl lg:text-4xl text-maroon"><Counter to={600} suffix="+" /></div>
              <div className="overline mt-1">Sq. Ft. Store</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
